import FeatureGates from '@atlaskit/feature-gate-js-client';
import { setBooleanFeatureFlagResolver } from '@atlaskit/platform-feature-flags';

/**
 * Pinned so their value can't change while the app is running.
 *
 * `@atlaskit/ds-lib` before 6.0.0 calls a different hook depending on these gates: `react-uid`
 * when off, `React.useId` when on. Statsig resolves them asynchronously, so a component can mount
 * on one hook and re-render on the other. React's hook slots are positional, so after the swap
 * `React.useId()` returns react-uid's state object rather than a string and ds-lib's `.replace`
 * dies with `l(...).replace is not a function`.
 * @see KIRBY-10780
 */
export const PINNED_PLATFORM_FEATURE_GATES: Record<string, boolean> = {
  'platform-dst-react-18-use-id': true,
  'platform-dst-react-18-use-id-selector-safe': true,
};

const pinned = new Map(Object.entries(PINNED_PLATFORM_FEATURE_GATES));

/** Resolves platform flags for Atlaskit packages, which don't use our `FeatureGateProvider`. */
export const resolvePlatformFeatureFlag = (name: string): boolean => {
  const pinnedValue = pinned.get(name);
  if (pinnedValue !== undefined) {
    return pinnedValue;
  }

  try {
    return FeatureGates.checkGate(name);
  } catch {
    // Throws until Statsig has initialised. Platform flags default to false when unresolvable,
    // and this runs inside an Atlaskit render, so it must not throw.
    return false;
  }
};

/** Must run before the first Atlaskit component renders. */
export const initPlatformFeatureFlagResolver = (): void => {
  setBooleanFeatureFlagResolver(resolvePlatformFeatureFlag);
};
